import tw, { styled } from "twin.macro";
import React, { useMemo, useCallback, useState } from "react";

import {
  GiftCardForm,
  type GiftCardFormProps,
  type GiftCardFormData,
  Image,
  currencyFromMoney,
} from "@bared/ui";

import RichText from "../RichText";
import { BackgroundContainer } from "@components/Styled/Container";
import { Sections } from "@components/Sections/Sections";
import { clsx } from "clsx";

import { Container } from "./Shared";

import { withProduct } from "./withProduct";

import { useCartAdd } from "~/hooks/useCart";

const Title = tw.h1`
  text-h2 leading-h1 mb-mini font-display md:text-[56px]
`;

const Description = styled(RichText)`
  ${tw`block mb-mini`}
  p {
    ${tw`text-left`}
  }
`;

export const GiftCard = withProduct(
  ({
    images,
    selectedVariant,
    productName,
    product,
    productSpecificContent,
  }) => {
    const [formValues, setFormValues] = useState<Partial<GiftCardFormData>>({});

    const variants = useMemo<GiftCardFormProps["variants"]>(
      () =>
        product.variants?.map((variant) => ({
          // Sometimes the variant ID is base 64 encoded ¯\_(ツ)_/¯
          id: variant.id.includes("=") ? atob(variant.id) : variant.id,
          price: variant.priceV2,
        })) ?? [],
      [product],
    );

    const { addToCart } = useCartAdd();
    const [activeIndex, setActiveIndex] = useState(0);

    const onSubmit = useCallback<GiftCardFormProps["onSubmit"]>(
      async (data) => {
        await addToCart({
          quantity: 1,
          merchandiseId: data.merchandiseId,
          attributes: [
            {
              key: "From",
              value: data.senderName,
            },
            {
              key: "Recipient email",
              value: data.recipientEmail,
            },
            {
              key: "Recipient name",
              value: data.recipientName,
            },
            {
              key: "Send on",
              value: data.sendDate,
            },
            {
              key: "__shopify_send_gift_card_to_recipient",
              value: "true",
            },
            ...(!!data.message
              ? [
                  {
                    key: "Message",
                    value: data.message,
                  },
                ]
              : []),
            {
              key: "_image",
              value: images[activeIndex].src,
            },
          ],
        });
      },
      [addToCart, images, activeIndex],
    );

    return (
      <>
        <BackgroundContainer background="light">
          <Container width="full" className="py-giga">
            <div className="flex w-full flex-wrap md:flex-nowrap md:gap-x-mini">
              <div className="md:w-4/10 md:px-0 2xl:pr-0 z-10 w-full px-midi md:mx-auto md:pb-midi md:pt-micro 2xl:px-tera">
                <Title>{productName}</Title>
                <Description html={product.descriptionHtml} />
                <p className="mb-micro">Choose a Design</p>
                <div className="mb-mini flex gap-pico">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveIndex(index)}
                      className={clsx(
                        "rounded h-[55px] w-[48px] overflow-hidden",
                        activeIndex === index && "border border-primary",
                      )}
                    >
                      <Image
                        source="shopify"
                        data={{
                          url: image.src,
                          altText: image.alt || "Gift card image",
                        }}
                        sizes="48px"
                        aspectRatio="48/55"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                <GiftCardForm
                  variants={variants}
                  onSubmit={onSubmit}
                  onPreviewUpdate={setFormValues}
                  className="mb-mini w-full"
                  initialMerchandiseId={selectedVariant?.id}
                />
              </div>
              <div className="md:w-5/10 w-full md:sticky md:top-[180px] md:self-start">
                <div className="sm:px-0 mx-auto px-midi pt-mini">
                  <h5 className="pb-mini text-hint tracking-heading">
                    GIFT CARD PREVIEW:
                  </h5>
                  <div className="border border-solid">
                    <Image
                      source="shopify"
                      data={{
                        url: images[activeIndex].src,
                        altText: images[activeIndex].src || "Gift card image",
                      }}
                      sizes="100vw"
                      aspectRatio="11/12"
                      className="bg-canvas"
                    />
                    <div className="text-lg bg-white p-midi text-left">
                      <p className="mb-micro">
                        Hi{" "}
                        <span
                          className={formValues.recipientName ? "" : "italic"}
                        >
                          {formValues.recipientName || "Recipient Name"}
                        </span>
                        , You've received a{" "}
                        {formValues.merchandiseId
                          ? currencyFromMoney(
                              variants.find(
                                (v) => v.id === formValues.merchandiseId,
                              )?.price ?? selectedVariant.priceV2,
                            ).format()
                          : ""}{" "}
                        Bared Footwear Gift Card.
                      </p>
                      <div className="mb-micro">
                        <h5 className="mb-micro text-hint tracking-heading">
                          FROM:
                        </h5>
                        <p className={formValues.senderName ? "" : "italic"}>
                          {formValues.senderName || "Sender Name"}
                        </p>
                      </div>
                      <div>
                        <h5 className="mb-micro text-hint tracking-heading">
                          MESSAGE:
                        </h5>
                        <p className={formValues.message ? "" : "italic"}>
                          {formValues.message ||
                            "Your optional message goes here."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </BackgroundContainer>
        {productSpecificContent?.content && (
          <div className="flex flex-col items-center">
            <Sections page={productSpecificContent} />
          </div>
        )}
      </>
    );
  },
);
